import React, { useEffect, useState } from 'react'
import { useDispatch, useStore } from 'react-redux'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { FetchErrorCodes } from '@e3dc-react/shell/Libs'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { useTypedSelector } from 'src/State/RootReducer'
import { setInstallation } from 'src/State/Storage/StorageActions'
import { useSaveInstallationAddress } from 'src/Libs/installation-details/hooks'
import { InstallationAddressFormValues } from 'src/Libs/installation-details/types'
import { fetchInstallation } from 'src/Libs/installations/api/installation.api'
import InstallationAddressFormDialog from './InstallationAddressFormDialog'
import { useUser } from 'src/Hooks/useUser'
import { isFlowTheme } from 'src/Libs/utils'
import { isDefined } from 'src/Libs/installation-details/utils'

/**
 * This component is used to get installation address.
 * shown only if the installation has no coordinates or country code, the current user is the owner of the installation,
 * and the installation status is NOT 'planned'.
 */
const AddInstallationAddressAndCoordinate: React.FC = () => {
  const { t } = useCustomTranslation()
  const dispatch = useDispatch()
  const authFetch = useAuthFetchWithResponse()
  const store = useStore()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const installation = useTypedSelector(state => state.storage.installation)
  const installationId = installation?.id
  const userId = useTypedSelector(state => state.auth.userId)
  const ownerAccountId = installation?.ownerAccountId?.toString()
  const { userData: user } = useUser()

  const hasCoordinates = isDefined(installation?.gpsLatitude) && isDefined(installation?.gpsLongitude)
  const hasCountryCode = isDefined(installation?.countryCode) && installation.countryCode !== ''
  const hasCoordinatesAndCountryCode = hasCoordinates && hasCountryCode
  const userAccountId = isFlowTheme ? user?.ssoId : userId
  const isOwner = isDefined(userAccountId) && isDefined(ownerAccountId) && String(userAccountId) === String(ownerAccountId)
  const isInstallationStatusPlanned = installation?.installationStatus === 'planned'

  const [isAddressFormOpen, setIsAddressFormOpen] = useState(false)
  const [errorCode, setErrorCode] = useState<number | false>(false)

  useEffect(() => {
    setErrorCode(false)

    if (hasCoordinatesAndCountryCode || !isOwner || isInstallationStatusPlanned) {
      setIsAddressFormOpen(false)
      return
    }

    /* delay the pop up to avoid flickering */
    const timer = setTimeout(() => {
      setIsAddressFormOpen(true)
    }, 1500)

    return () => clearTimeout(timer)
  }, [hasCoordinatesAndCountryCode, isOwner, isInstallationStatusPlanned, installationId])

  const { mutate: saveAddress, isPending } = useSaveInstallationAddress()

  const handleSaveAddress = async (): Promise<void> => {
    enqueueSuccessStack(t('installation-details.installation-address.success'))
    setIsAddressFormOpen(false)
    /* the modal closes without waiting for the refetch. store will be updated once the fetch completes */
    if (installationId) {
      const result = await fetchInstallation(installationId, authFetch)
      /* skip dispatch if the user switched installations while the fetch was in-flight */
      if (result.value && store.getState().storage.installation?.id === installationId) {
        dispatch(setInstallation(result.value))
      }
    }
  }

  const handleSubmitAddress = (data: InstallationAddressFormValues): void => {
    if (!installationId) return

    setErrorCode(false)

    const payload: InstallationAddressFormValues = {
      streetName: data.streetName,
      streetNumber: data.streetNumber,
      city: data.city,
      zip: data.zip,
      countryCode: data.countryCode,
    }

    saveAddress(
      { installationId, payload },
      {
        onSuccess: handleSaveAddress,
        onError: error => {
          const errno = (error as AuthFetchError).response?.data?.errno
          if (errno === FetchErrorCodes.InvalidAddressZip || errno === FetchErrorCodes.InvalidAddressCity) {
            setErrorCode(errno)
          } else {
            enqueueErrorStack(t('installation-details.installation-address.error'))
          }
        },
      }
    )
  }

  const handleClose = (): void => {
    setIsAddressFormOpen(false)
    setErrorCode(false)
  }

  return (
    <>
      {isAddressFormOpen && (
        <InstallationAddressFormDialog
          key={installationId}
          isOpen={isAddressFormOpen}
          onClose={handleClose}
          onSubmit={handleSubmitAddress}
          isPending={isPending}
          errorCode={errorCode}
        />
      )}
    </>
  )
}

export default AddInstallationAddressAndCoordinate
